import React, { useState } from 'react';
import {
  Globe, Layout, Palette, FileText, Eye, Settings, Check, X,
  ExternalLink, Copy, RefreshCw, ChevronRight, Monitor, Smartphone,
  Tablet, Image, Type, MapPin, Phone, Mail, Clock, Search,
  Zap, Shield, Truck, Package, DollarSign, HelpCircle, Users,
  Edit2, Plus, Trash2, GripVertical, Save, AlertCircle
} from 'lucide-react';

// Mock templates
const websiteTemplates = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean, minimalist design with bold typography',
    preview: '/templates/modern.png',
    colors: { primary: '#8b5cf6', secondary: '#06b6d4', accent: '#f59e0b' },
    features: ['Animated hero', 'Glass morphism', 'Dark mode ready']
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Professional and trustworthy corporate look',
    preview: '/templates/classic.png',
    colors: { primary: '#1e40af', secondary: '#059669', accent: '#dc2626' },
    features: ['Traditional layout', 'High contrast', 'Print friendly']
  },
  {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'Colorful and energetic with gradient accents',
    preview: '/templates/vibrant.png',
    colors: { primary: '#ec4899', secondary: '#8b5cf6', accent: '#06b6d4' },
    features: ['Gradient backgrounds', 'Playful animations', 'Bold colors']
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Ultra-clean with focus on content',
    preview: '/templates/minimal.png',
    colors: { primary: '#18181b', secondary: '#71717a', accent: '#3b82f6' },
    features: ['Whitespace focused', 'Typography driven', 'Fast loading']
  },
  {
    id: 'caribbean',
    name: 'Caribbean',
    description: 'Warm, tropical theme perfect for island couriers',
    preview: '/templates/caribbean.png',
    colors: { primary: '#0891b2', secondary: '#059669', accent: '#fbbf24' },
    features: ['Island vibes', 'Warm palette', 'Friendly feel']
  }
];

// Website pages configuration
const defaultPages = [
  { id: 'home', name: 'Home', enabled: true, required: true },
  { id: 'track', name: 'Track Package', enabled: true, required: true },
  { id: 'pricing', name: 'Pricing', enabled: true, required: false },
  { id: 'services', name: 'Services', enabled: true, required: false },
  { id: 'faq', name: 'FAQ', enabled: true, required: false },
  { id: 'contact', name: 'Contact', enabled: true, required: false },
];

export default function TenantWebsiteBuilder() {
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [websiteEnabled, setWebsiteEnabled] = useState(true);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pages, setPages] = useState(defaultPages);
  
  // Website settings
  const [websiteSettings, setWebsiteSettings] = useState({
    domain: 'www.fastshipjamaica.com',
    domainVerified: true,
    sslEnabled: true,
    // Branding
    logo: null,
    favicon: null,
    primaryColor: '#8b5cf6',
    secondaryColor: '#06b6d4',
    // Content
    companyName: 'FastShip Jamaica',
    tagline: 'Fast & Reliable Courier Services',
    heroTitle: 'Ship Anywhere in Jamaica',
    heroSubtitle: 'Track your packages in real-time with our trusted courier service',
    aboutText: 'FastShip Jamaica has been delivering packages across the island since 2015. We pride ourselves on speed, reliability, and excellent customer service.',
    // Contact
    phone: '+1 (876) 555-0123',
    email: 'info@fastshipjamaica.com',
    address: '123 Harbour Street, Kingston, Jamaica',
    hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM',
    // Social
    facebook: 'https://facebook.com/fastshipja',
    instagram: 'https://instagram.com/fastshipja',
    whatsapp: '+18765550123',
  });

  const [contentSections, setContentSections] = useState([
    { id: 'services', title: 'Our Services', items: [
      { icon: 'Truck', title: 'Express Delivery', desc: 'Same-day delivery within Kingston' },
      { icon: 'Package', title: 'Package Forwarding', desc: 'Ship from USA to Jamaica' },
      { icon: 'Shield', title: 'Insured Shipping', desc: 'Full coverage for your packages' },
    ]},
    { id: 'areas', title: 'Service Areas', items: [
      'Kingston', 'Montego Bay', 'Ocho Rios', 'Portmore', 'Spanish Town', 'Mandeville'
    ]},
  ]);

  const [faqItems, setFaqItems] = useState([
    { q: 'How do I track my package?', a: 'Enter your tracking number on our Track Package page to see real-time updates.' },
    { q: 'What are your delivery times?', a: 'Kingston: Same day, Other parishes: 1-3 business days.' },
    { q: 'Do you offer insurance?', a: 'Yes, all packages are insured up to $10,000 JMD. Additional coverage available.' },
    { q: 'How is pricing calculated?', a: 'Pricing is based on weight tiers. See our Pricing page for details.' },
  ]);

  const handleTogglePage = (pageId) => {
    setPages(pages.map(p => 
      p.id === pageId && !p.required ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const tabs = [
    { id: 'templates', label: 'Templates', icon: Layout },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'pages', label: 'Pages', icon: Globe },
    { id: 'domain', label: 'Domain', icon: Settings },
  ];

  return (
    <div className="tenant-website-builder" data-testid="tenant-website-builder">
      {/* Header */}
      <div className="builder-header">
        <div className="header-info">
          <h2>Website Builder</h2>
          <p>Create and customize your customer-facing website</p>
        </div>
        <div className="header-actions">
          <div className="website-toggle">
            <span className="toggle-label">Website Status:</span>
            <button 
              className={`toggle-btn ${websiteEnabled ? 'active' : ''}`}
              onClick={() => setWebsiteEnabled(!websiteEnabled)}
              data-testid="website-toggle"
            >
              <span className="toggle-slider"></span>
              <span className="toggle-text">{websiteEnabled ? 'Active' : 'Inactive'}</span>
            </button>
          </div>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowPreviewModal(true)}
            data-testid="preview-btn"
          >
            <Eye size={16} /> Preview
          </button>
          <button className="btn btn-primary" data-testid="publish-btn">
            <Globe size={16} /> Publish Changes
          </button>
        </div>
      </div>

      {/* Status Bar */}
      {websiteEnabled && (
        <div className="website-status-bar">
          <div className="status-item">
            <Globe size={16} />
            <span>Live at:</span>
            <a href={`https://${websiteSettings.domain}`} target="_blank" rel="noopener noreferrer">
              {websiteSettings.domain}
              <ExternalLink size={12} />
            </a>
          </div>
          <div className="status-item">
            {websiteSettings.sslEnabled ? (
              <><Shield size={16} className="success" /> SSL Enabled</>
            ) : (
              <><AlertCircle size={16} className="warning" /> SSL Pending</>
            )}
          </div>
          <div className="status-item">
            <Layout size={16} />
            <span>Template: {websiteTemplates.find(t => t.id === selectedTemplate)?.name}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="builder-content">
        {/* Tabs */}
        <div className="builder-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="templates-section">
              <h3>Choose a Template</h3>
              <p className="section-desc">Select a design template for your website. You can customize colors and content after.</p>
              
              <div className="templates-grid">
                {websiteTemplates.map(template => (
                  <div 
                    key={template.id}
                    className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTemplate(template.id)}
                    data-testid={`template-${template.id}`}
                  >
                    <div className="template-preview">
                      <div className="preview-placeholder" style={{
                        background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`
                      }}>
                        <Layout size={48} />
                      </div>
                      {selectedTemplate === template.id && (
                        <div className="selected-badge">
                          <Check size={16} /> Selected
                        </div>
                      )}
                    </div>
                    <div className="template-info">
                      <h4>{template.name}</h4>
                      <p>{template.description}</p>
                      <div className="template-features">
                        {template.features.map((f, i) => (
                          <span key={i} className="feature-tag">{f}</span>
                        ))}
                      </div>
                      <div className="template-colors">
                        {Object.values(template.colors).map((color, i) => (
                          <span key={i} className="color-dot" style={{ background: color }}></span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="branding-section">
              <h3>Brand Customization</h3>
              <p className="section-desc">Upload your logo and customize colors to match your brand.</p>
              
              <div className="branding-grid">
                <div className="branding-card">
                  <h4><Image size={18} /> Logo</h4>
                  <div className="upload-zone">
                    <Image size={32} />
                    <span>Drop logo here or click to upload</span>
                    <span className="upload-hint">Recommended: 200x60px, PNG or SVG</span>
                  </div>
                </div>

                <div className="branding-card">
                  <h4><Image size={18} /> Favicon</h4>
                  <div className="upload-zone small">
                    <Image size={24} />
                    <span>32x32px icon</span>
                  </div>
                </div>

                <div className="branding-card colors">
                  <h4><Palette size={18} /> Colors</h4>
                  <div className="color-inputs">
                    <div className="color-input">
                      <label>Primary</label>
                      <div className="color-picker">
                        <input 
                          type="color" 
                          value={websiteSettings.primaryColor}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, primaryColor: e.target.value})}
                        />
                        <span>{websiteSettings.primaryColor}</span>
                      </div>
                    </div>
                    <div className="color-input">
                      <label>Secondary</label>
                      <div className="color-picker">
                        <input 
                          type="color" 
                          value={websiteSettings.secondaryColor}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, secondaryColor: e.target.value})}
                        />
                        <span>{websiteSettings.secondaryColor}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="branding-card full-width">
                  <h4><Type size={18} /> Company Info</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Company Name</label>
                      <input 
                        type="text" 
                        value={websiteSettings.companyName}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, companyName: e.target.value})}
                        data-testid="company-name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tagline</label>
                      <input 
                        type="text" 
                        value={websiteSettings.tagline}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, tagline: e.target.value})}
                        data-testid="tagline"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="content-section">
              <h3>Website Content</h3>
              <p className="section-desc">Edit the text and information displayed on your website.</p>

              <div className="content-cards">
                {/* Hero Section */}
                <div className="content-card">
                  <div className="card-header">
                    <h4><Zap size={18} /> Hero Section</h4>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Hero Title</label>
                      <input 
                        type="text" 
                        value={websiteSettings.heroTitle}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, heroTitle: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Subtitle</label>
                      <textarea 
                        rows={2}
                        value={websiteSettings.heroSubtitle}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, heroSubtitle: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* About Section */}
                <div className="content-card">
                  <div className="card-header">
                    <h4><Users size={18} /> About Section</h4>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>About Text</label>
                      <textarea 
                        rows={4}
                        value={websiteSettings.aboutText}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, aboutText: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="content-card">
                  <div className="card-header">
                    <h4><Phone size={18} /> Contact Information</h4>
                  </div>
                  <div className="card-body">
                    <div className="form-grid">
                      <div className="form-group">
                        <label><Phone size={14} /> Phone</label>
                        <input 
                          type="text" 
                          value={websiteSettings.phone}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, phone: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label><Mail size={14} /> Email</label>
                        <input 
                          type="email" 
                          value={websiteSettings.email}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, email: e.target.value})}
                        />
                      </div>
                      <div className="form-group full">
                        <label><MapPin size={14} /> Address</label>
                        <input 
                          type="text" 
                          value={websiteSettings.address}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, address: e.target.value})}
                        />
                      </div>
                      <div className="form-group full">
                        <label><Clock size={14} /> Business Hours</label>
                        <input 
                          type="text" 
                          value={websiteSettings.hours}
                          onChange={(e) => setWebsiteSettings({...websiteSettings, hours: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="content-card">
                  <div className="card-header">
                    <h4><HelpCircle size={18} /> FAQ Items</h4>
                    <button className="btn btn-sm btn-ghost">
                      <Plus size={14} /> Add FAQ
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="faq-list">
                      {faqItems.map((faq, index) => (
                        <div key={index} className="faq-item">
                          <div className="faq-header">
                            <GripVertical size={14} className="drag-handle" />
                            <input 
                              type="text" 
                              value={faq.q}
                              onChange={(e) => {
                                const updated = [...faqItems];
                                updated[index].q = e.target.value;
                                setFaqItems(updated);
                              }}
                              placeholder="Question"
                            />
                            <button className="btn-icon delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <textarea 
                            value={faq.a}
                            onChange={(e) => {
                              const updated = [...faqItems];
                              updated[index].a = e.target.value;
                              setFaqItems(updated);
                            }}
                            placeholder="Answer"
                            rows={2}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Service Areas */}
                <div className="content-card">
                  <div className="card-header">
                    <h4><MapPin size={18} /> Service Areas</h4>
                  </div>
                  <div className="card-body">
                    <div className="service-areas-grid">
                      {contentSections.find(s => s.id === 'areas')?.items.map((area, i) => (
                        <div key={i} className="area-tag">
                          {area}
                          <button className="remove-btn"><X size={12} /></button>
                        </div>
                      ))}
                      <button className="add-area-btn">
                        <Plus size={14} /> Add Area
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="pages-section">
              <h3>Manage Pages</h3>
              <p className="section-desc">Enable or disable pages on your website.</p>

              <div className="pages-list">
                {pages.map(page => (
                  <div key={page.id} className={`page-item ${page.enabled ? 'enabled' : 'disabled'}`}>
                    <div className="page-info">
                      <GripVertical size={16} className="drag-handle" />
                      <div className="page-icon">
                        {page.id === 'home' && <Globe size={18} />}
                        {page.id === 'track' && <Search size={18} />}
                        {page.id === 'pricing' && <DollarSign size={18} />}
                        {page.id === 'services' && <Truck size={18} />}
                        {page.id === 'faq' && <HelpCircle size={18} />}
                        {page.id === 'contact' && <Phone size={18} />}
                      </div>
                      <div className="page-details">
                        <span className="page-name">{page.name}</span>
                        <span className="page-url">/{page.id === 'home' ? '' : page.id}</span>
                      </div>
                    </div>
                    <div className="page-actions">
                      {page.required && <span className="required-badge">Required</span>}
                      <button 
                        className={`toggle-switch ${page.enabled ? 'on' : 'off'}`}
                        onClick={() => handleTogglePage(page.id)}
                        disabled={page.required}
                        data-testid={`toggle-${page.id}`}
                      >
                        <span className="switch-slider"></span>
                      </button>
                      <button className="btn-icon" data-testid={`edit-${page.id}`}>
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="btn btn-secondary add-page-btn" data-testid="add-page-btn">
                <Plus size={16} /> Add Custom Page
              </button>
            </div>
          )}

          {/* Domain Tab */}
          {activeTab === 'domain' && (
            <div className="domain-section">
              <h3>Domain Settings</h3>
              <p className="section-desc">Configure your custom domain for the website.</p>

              <div className="domain-card">
                <div className="domain-header">
                  <Globe size={24} />
                  <div>
                    <h4>Custom Domain</h4>
                    <p>Connect your own domain to your customer website</p>
                  </div>
                </div>

                <div className="domain-input-section">
                  <div className="form-group">
                    <label>Your Domain</label>
                    <div className="domain-input-wrapper">
                      <span className="protocol">https://</span>
                      <input 
                        type="text" 
                        value={websiteSettings.domain}
                        onChange={(e) => setWebsiteSettings({...websiteSettings, domain: e.target.value})}
                        placeholder="www.yourdomain.com"
                        data-testid="domain-input"
                      />
                      {websiteSettings.domainVerified ? (
                        <span className="verified-badge"><Check size={14} /> Verified</span>
                      ) : (
                        <button className="btn btn-sm btn-primary">Verify</button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="dns-instructions">
                  <h4>DNS Configuration</h4>
                  <p>Add the following DNS records to your domain registrar:</p>
                  
                  <div className="dns-records">
                    <div className="dns-record">
                      <div className="record-type">CNAME</div>
                      <div className="record-details">
                        <span className="record-name">www</span>
                        <span className="record-value">cname.boltnexus.com</span>
                        <button className="copy-btn"><Copy size={14} /></button>
                      </div>
                    </div>
                    <div className="dns-record">
                      <div className="record-type">A</div>
                      <div className="record-details">
                        <span className="record-name">@</span>
                        <span className="record-value">76.76.21.21</span>
                        <button className="copy-btn"><Copy size={14} /></button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ssl-section">
                  <div className="ssl-status">
                    <Shield size={20} />
                    <div>
                      <h4>SSL Certificate</h4>
                      <p>{websiteSettings.sslEnabled ? 'Your website is secured with HTTPS' : 'SSL certificate is being provisioned...'}</p>
                    </div>
                    <span className={`ssl-badge ${websiteSettings.sslEnabled ? 'active' : 'pending'}`}>
                      {websiteSettings.sslEnabled ? 'Active' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Website Preview</h3>
                <span className="modal-subtitle">{websiteSettings.domain}</span>
              </div>
              <div className="preview-controls">
                <div className="device-switcher">
                  <button 
                    className={previewDevice === 'desktop' ? 'active' : ''}
                    onClick={() => setPreviewDevice('desktop')}
                  >
                    <Monitor size={18} />
                  </button>
                  <button 
                    className={previewDevice === 'tablet' ? 'active' : ''}
                    onClick={() => setPreviewDevice('tablet')}
                  >
                    <Tablet size={18} />
                  </button>
                  <button 
                    className={previewDevice === 'mobile' ? 'active' : ''}
                    onClick={() => setPreviewDevice('mobile')}
                  >
                    <Smartphone size={18} />
                  </button>
                </div>
                <button className="close-btn" onClick={() => setShowPreviewModal(false)}>
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="modal-body preview-body">
              <div className={`preview-frame ${previewDevice}`}>
                <div className="preview-content" style={{
                  '--primary-color': websiteSettings.primaryColor,
                  '--secondary-color': websiteSettings.secondaryColor,
                }}>
                  {/* Mock Website Preview */}
                  <div className="mock-website">
                    <header className="mock-header">
                      <div className="mock-logo">{websiteSettings.companyName}</div>
                      <nav className="mock-nav">
                        {pages.filter(p => p.enabled).map(p => (
                          <span key={p.id}>{p.name}</span>
                        ))}
                      </nav>
                    </header>
                    <div className="mock-hero">
                      <h1>{websiteSettings.heroTitle}</h1>
                      <p>{websiteSettings.heroSubtitle}</p>
                      <div className="mock-tracking-box">
                        <Search size={18} />
                        <span>Enter tracking number...</span>
                        <button>Track</button>
                      </div>
                    </div>
                    <div className="mock-services">
                      <h2>Our Services</h2>
                      <div className="mock-service-cards">
                        <div className="mock-card">
                          <Truck size={24} />
                          <h3>Express Delivery</h3>
                        </div>
                        <div className="mock-card">
                          <Package size={24} />
                          <h3>Package Forwarding</h3>
                        </div>
                        <div className="mock-card">
                          <Shield size={24} />
                          <h3>Insured Shipping</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </button>
              <a 
                href={`https://${websiteSettings.domain}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <ExternalLink size={14} /> Open Live Site
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
